import { fetchLatestBuildVersion } from "./buildVersion";
import { getSessionUser } from "./authUtil";
import { getCurrentServerTime } from "./util";
import Configuration from "../config";
import { SERVER_TIME_OFFSET } from "./restUtil";
import { BROWSER_TAB_ID_KEY } from "utils/storage";

export const LOG_LEVEL = {
  INFO: "info",
  WARNING: "warning",
  ERROR: "error",
};

const LOGGER_URL = "https://bc6c71e6c0e64fe19da19315c10aa826@sentry.io/283084";
const DEFAULT_LEVEL = LOG_LEVEL.INFO;
const LESSON_TAG_NAME = "lesson.id";

export async function configLogger() {
  if (!window.Raven) {
    return;
  }

  try {
    const buildVersion = await fetchLatestBuildVersion();
    window.Raven.setRelease(buildVersion);
  } catch (e) {
    window.Raven.setRelease("not available");
  }

  window.Raven.config(LOGGER_URL).install();
}

function getNetworkInfo() {
  if (navigator && navigator.connection) {
    const networkInfo = navigator.connection;
    return {
      downlink: networkInfo.downlink,
      downlinkMax: networkInfo.downlinkMax,
      effectiveType: networkInfo.effectiveType,
      rtt: networkInfo.rtt,
      type: networkInfo.type,
    };
  }

  return {};
}

export function saveUserContext(user) {
  const context = {
    id: user.id,
    email: user.profile.email,
    first_name: user.profile.first_name,
    last_name: user.profile.last_name,
    current_time: new Date().toString(),
    current_server_time: getCurrentServerTime(),
    utc_offset: user.profile.timezone ? user.profile.timezone.utc_offset : "",
  };

  const timeOffset = localStorage.getItem(SERVER_TIME_OFFSET);
  if (timeOffset) {
    context.time_offset = timeOffset;
  }

  context.network_info = getNetworkInfo();

  window.Raven && window.Raven.setUserContext(context);
}

export function clearUserContext() {
  window.Raven && window.Raven.setUserContext();
}

export function log(message, data, level) {
  setBrowserTabId();
  const addData = {
    level: level || DEFAULT_LEVEL,
  };
  let extra = {};
  if (data) {
    extra = data;
  }
  const user = getSessionUser();
  if (user) {
    saveUserContext(user); // To avoid empty context after page refresh.
    const userMessageIdentificator = user.profile ? user.profile.email : user.id;
    message = userMessageIdentificator + ": " + message;
  }
  message += " (" + getCurrentServerTime().toJSON() + ")"; // To prevent events grouping.
  addData.extra = extra;
  window.Raven && window.Raven.captureMessage(message, addData);
}

export function logInfo(message, data) {
  log(message, data, LOG_LEVEL.INFO);
  if (Configuration.ENABLE_CONSOLE_LOGGING) {
    // eslint-disable-next-line no-console
    console.log(message, data);
  }
}

export function logError(message, data) {
  log(message, data, LOG_LEVEL.ERROR);
  if (Configuration.ENABLE_CONSOLE_LOGGING) {
    // eslint-disable-next-line no-console
    console.error(message, data);
  }
}

export function logWarning(message, data) {
  log(message, data, LOG_LEVEL.WARNING);
  if (Configuration.ENABLE_CONSOLE_LOGGING) {
    // eslint-disable-next-line no-console
    console.warn(message, data);
  }
}

export function setLessonTag(id) {
  const context = {};
  context[LESSON_TAG_NAME] = id;
  window.Raven && window.Raven.setTagsContext(context);
}

export function removeLessonTag() {
  if (!window.Raven) {
    return;
  }
  const context = window.Raven.getContext(); // Note: Do not mutate context directly.
  const tags = { ...context.tags };
  delete tags[LESSON_TAG_NAME];
  window.Raven.setTagsContext(); // Clear all current tags from the context.
  window.Raven.setTagsContext(tags); // Add back the tags you want to keep.
}

function setBrowserTabId() {
  const browserTabId = sessionStorage.getItem(BROWSER_TAB_ID_KEY);
  if (!browserTabId) {
    return;
  }
  const context = {};
  context[BROWSER_TAB_ID_KEY] = browserTabId;
  window.Raven && window.Raven.setTagsContext(context);
}
